/* PC Component Library
 * Author: Preetha Ravichandran
 * Updated On: 11-07-2022
 *
 */
/*Imports*/
import React, { useEffect, useRef, useState } from "react";
import { Loader, ThemeType, toggleTheme } from "@scuf/common";
import { useParams, useNavigate } from "react-router-dom";
import {
  IDisclaimerText,
  IEnergyCarbonGraphData,
} from "../../models/ISignageViewModel";
import "./DigitalSignageViewContainer.scss";
import { Theme } from "../../shared/enum";
import {
  DSIBOSecondPage,
  DSIBOThirdComponent,
  DSNotConfiguredLandscape,
  Footer,
  SlideshowFirstPage,
} from "@PremiumCommercial/PremiumCommercial.pc.digitalsignage.remote";
import DigitalSignageAreaGraph, {
  IEnergyConsumptionData,
  IPerformanceData,
} from "./DigitalSignageAreaGraph";
import DigitalSignageAreaFooter, {
  isNotNegative,
} from "./DigitalSignageAreaFooter";
import DigitalSignageAreaHeader from "./DigitalSignageAreaHeader";
import { ConversionType } from "@EMaaS/EMaaS.consup.react.widget.energymanagement";

interface ISiteInformation {
  siteId: string;
  siteName: string | null;
  logoUrl: string | null;
}

interface IPerformance {
  startDate: string;
  value: number | null;
}

interface ISummary {
  actual: number;
  baseline: number;
}

interface IEnergyData {
  actualPerformance: IPerformance[];
  baselinePerformance: IPerformance[];
  unit: string;
  isEnabled: boolean;
  summary: ISummary;
}

interface ISavingsData {
  actualCost: IPerformance[];
  baselineCost: IPerformance[];
  unit: string;
  isEnabled: boolean;
  summary: ISummary;
}

export interface IDigitalSignageData {
  siteInformation: ISiteInformation;
  electricity?: IEnergyData | null;
  gas?: IEnergyData | null;
  water?: IEnergyData | null;
  carbon?: IEnergyData | null;
  savings?: ISavingsData | null;
  absorptionCount: number;
  gallonsOfGas: number;
  carsRemoved: number;
  slideTransitionInterval?:string;
}

const originalFetch = require("isomorphic-fetch");
const fetchURL = require("fetch-retry")(originalFetch);
const theme = false; //if true = black and false = light

function DigitalSignageViewContainer() {
  let siteId = "";
  const number120 = 120;
  const number1000 = 1000;
  const number60 = 60;
  const number3 = 3;
  const number2 = 2;
  const number400 = 400;
  const number404 = 404;
  const number408 = 408;
  const number500 = 500;
  const number502 = 502;
  const number503 = 503;
  const number504 = 504;
  const number522 = 522;
  const number524 = 524;
  const number10000 = 10000;
  const retryTimer = number120 * number1000;
  let deviceActivationRetryCount = 0;
  let { activationCode } = useParams();
  const navigate = useNavigate();
  if (activationCode) {
    localStorage.setItem("ActivationCode", activationCode);
  }
  const [siteImage, setSiteImage] = useState("");
  const [isTemaplateAvailable, setIsTemaplateAvailable] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [screenOption, setScreenOption] = useState("Portrait");
  const [theme, setTheme] = useState("Dark");
  const [graphDataErrorScreen, setGraphDataErrorScreen] = useState(false);
  const [errorScreen, setErrorScreen] = useState(false);
  const [activationCodeErrorScreen, setActiationCodeErrorScreen] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [activationCodeLoading, setActivationCodeLoading] = useState(false);
  const [deviceId, setDeviceId] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [token, setToken] = useState("");
  const [energyCarbonDataConsumption, setEnergyCarbonDataConsumption] =
    useState<IEnergyCarbonGraphData>();
  const [getDIgitalSignageData, setDIgitalSignageData] =
    useState<IDigitalSignageData>();
  const [currentComponentIndex, setCurrentComponentIndex] = useState<number | undefined>(undefined);
  const [titleErrMsg, SetTitleErrMsg] = useState("Oops! Something went wrong");
  const [textErrMsg, setTextErrMsg] = useState(
    "Something went wrong while trying to load the signage."
  );
  const [displayErrorScreen, setDisplayErrorScreen] = useState(false);
  // Launch Darkly - To manage Digital Signage visibility
  const totalNoOfDays = window.ENV.DS_DATA_FOR_NO_OF_DAYS;
  const templateDataRefreshTimer = window.ENV.TEMPLATE_DATA_REFRESH_TIMER;

  //let displayErrorScreen: boolean = false;
  let dsLoading: boolean;

  useEffect(() => {
    const localStorageDeviceId = localStorage.getItem("deviceId");
    if (!localStorageDeviceId) {
      getTokenTemplateData("");
    } else {
      setDeviceId(localStorageDeviceId);
      getTokenTemplateData(localStorageDeviceId);
    }
  }, []);

  useEffect(() => {
    if (
      !loading &&
      !graphLoading &&
      !activationCodeLoading &&
      !displayErrorScreen
    ) {
      let timer = getDIgitalSignageData && getDIgitalSignageData.slideTransitionInterval
        ? getDIgitalSignageData.slideTransitionInterval : '7';
      const interval = setTimeout(() => {
        startInfiniteSlider();
      }, Number(timer) * number1000);
      return () => {
        clearInterval(interval);
      };
    }
  });

  useEffect(() => {
    if (templateId) {
      const getProfileData = async () => {
        await getTemplateForSite(true, templateId);
      };
      getProfileData();
      getDigitalSignageGraphData(templateId, true);
      // Auto-Refresh implementation
      // octopus changes

      let interval = setInterval(() => {
        getTemplateForSite(false, templateId);
        getDigitalSignageGraphData(templateId, false);
      }, templateDataRefreshTimer * number60 * number1000);

      // clear interval
      return () => {
        clearInterval(interval);
      };
    }
  }, [templateId]);
  const usePrevious = (value: string) => {
    const ref = useRef("");
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const prevToken = usePrevious(token);
  useEffect(() => {
    if (token && token !== "" && prevToken !== token && templateId) {
      getTemplateForSite(true, templateId);
      getDigitalSignageGraphData(templateId, true);
    }
  }, [token]);

  let startDeviceActivationTimer = () => {
    const timerforTokenData = setTimeout(() => {
      getTokenTemplateData(deviceId, false);
    }, retryTimer);
    return () => clearTimeout(timerforTokenData);
  };
  // To Disable the APIs
  const disableTheAPIs = () => {
    setGraphLoading(false);
    setLoading(false);
  };
  const getTokenTemplateData = async (
    updatedDeviceId: string,
    tokenLoading = true
  ) => {
    setActivationCodeLoading(tokenLoading);
    const storedActivationCode = localStorage.getItem("ActivationCode");
    let headers = {};
    if (updatedDeviceId !== "") {
      headers = {
        "X-DeviceId": updatedDeviceId,
      };
    }
    try {
      const responseData = await fetch(
        `${window.ENV.BASE_URL_API_GATEWAY}/api/Signage/validateActivationCode/${storedActivationCode}`,
        {
          headers: headers,
        }
      );
      if (responseData && responseData.ok) {
        const data = await responseData.json();
        setActiationCodeErrorScreen(false);
        setDeviceId(data.deviceId);
        setDeviceName(data.deviceName);
        localStorage.setItem("deviceId", data.deviceId);
        setToken(data.token);
        setTemplateId(data.templateId);
        setLoading(true);
        setGraphLoading(true);
        setActivationCodeLoading(false);
      } else {
        setActivationCodeLoading(false);
        setActiationCodeErrorScreen(true);
        const data = await responseData.json();
        switch (data.code) {
          case "D005":
            disableTheAPIs();
            SetTitleErrMsg("Digital signage not available");
            setTextErrMsg(
              "Something went wrong while trying to load this sign. \n The configuration of your digital signage is invalid."
            );
            break;
          case "D012":
            disableTheAPIs();
            SetTitleErrMsg("Digital signage expired");
            setTextErrMsg("Your digital signage has expired.");
            break;
          default:
            SetTitleErrMsg("Oops! Something went wrong");
            setTextErrMsg(
              "Something went wrong while trying to load the signage."
            );
            deviceActivationRetryCount = deviceActivationRetryCount + 1;
            if (deviceActivationRetryCount < number3) {
              startDeviceActivationTimer();
            } else {
              disableTheAPIs();
            }
        }
      }
    } catch (err) {
      setActivationCodeLoading(false);
      setActiationCodeErrorScreen(true);
      SetTitleErrMsg("Oops! Something went wrong");
      setTextErrMsg("Something went wrong while trying to load the signage.");
      deviceActivationRetryCount = deviceActivationRetryCount + 1;
      if (deviceActivationRetryCount < number3) {
        startDeviceActivationTimer();
      } else {
        disableTheAPIs();
      }
    }
  };

  const startInfiniteSlider = () => {
    if (!getDIgitalSignageData) return;
  
    const { electricity, gas, water, carbon, savings } = getDIgitalSignageData;
  
    // Create an array of enabled components
    const enabledComponents = [
      electricity?.isEnabled && 0, // Electricity corresponds to index 0
      gas?.isEnabled && 1,         // Gas corresponds to index 1
      water?.isEnabled && 2,       // Water corresponds to index 2
      carbon?.isEnabled && 3,      // Carbon corresponds to index 3
      savings?.isEnabled && 4,     // Savings corresponds to index 4
    ].filter((index) => index !== false); // Filter out disabled components

    const enabledComponentsRemoveUndefined = enabledComponents.filter(
      (val) => val !== undefined
    );
    // Find the current index in the enabled components array
    const currentIndexInEnabled = enabledComponentsRemoveUndefined.indexOf(currentComponentIndex ?? -1);
  
    // Determine the next index
    const nextIndex =
      currentIndexInEnabled === enabledComponentsRemoveUndefined.length - 1
        ? enabledComponentsRemoveUndefined[0] // If at the last index, go back to the first
        : enabledComponentsRemoveUndefined[currentIndexInEnabled + 1]; // Otherwise, go to the next index
    setCurrentComponentIndex(nextIndex);
  };

  const setAppTheme = (appTheme: string) => {
    if (appTheme.toLowerCase() === Theme.LIGHT.toLowerCase()) {
      toggleTheme(ThemeType.Light);
    } else {
      toggleTheme(ThemeType.Dark);
    }
  };

  const getSignageTemplateForSite = async (updatedTemplateId: string) => {
    try {
      const url =
        `${window.ENV.BASE_URL_API_GATEWAY}` +
        "/api/CompositeService/GetProfileConfiguratorDetails?templateId=" +
        updatedTemplateId;
      return await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-NodeId": siteId,
          "X-DeviceId": deviceId,
        },
      });
    } catch (error) {
      return error;
    }
  };
  const getErrorMsg = (errMsgCode: string) => {
    let returnMsg: string = "";
    if (errMsgCode === "DS002") {
      returnMsg = "Digital Signage Not found";
    } else if (errMsgCode === "DS003") {
      returnMsg = "Digital Signage Deleted";
    } else if (errMsgCode === "DS004") {
      returnMsg = "Digital Signage Disabled";
    } else {
      returnMsg = "Digital Signage not found";
    }
    return returnMsg;
  };
  const checkSiteIcon = (siteIcon: string) => {
    if (siteIcon) {
      const sitebase64 = `data:image/png;base64,${siteIcon}`;
      setSiteImage(sitebase64);
    }
  };

  const afterFetchResponse = async () => {
    const url =
      `${window.ENV.BASE_URL_API_GATEWAY}` +
      "/api/CompositeService/GetProfileConfiguratorDetails?templateId=" +
      templateId;
    const retyresponse = await retryFunction(url, "POST");
    if (retyresponse.isSuccess) {
      setErrorScreen(false);
      const profileConfiguratorDetails =
        retyresponse.data.profileConfiguratorDetails;
      setTheme(profileConfiguratorDetails.theme);
      setAppTheme(profileConfiguratorDetails.theme);
      if (profileConfiguratorDetails.siteTimeZone) {
        // setSiteTimezone(profileConfiguratorDetails.siteTimeZone);
      }
      if (profileConfiguratorDetails.siteIcon) {
        const sitebase64 = `data:image/png;base64,${profileConfiguratorDetails.siteIcon}`;
        setSiteImage(sitebase64);
      }
      setScreenOption(profileConfiguratorDetails.screenOption);
    } else {
      setIsTemaplateAvailable(false);
      const errorMsg = getErrorMsg(retyresponse.code);
      setErrorMessage(errorMsg);
    }
  };

  const getTemplateForSite = async (
    loaderValue: boolean,
    templateIdForSite: string
  ) => {
    setErrorScreen(false);
    setLoading(loaderValue);
    const response: any = await getSignageTemplateForSite(templateIdForSite);
    try {
      if (response.ok) {
        setLoading(false);
        const res = await response.json();
        if (res.isSuccess) {
          const profileConfiguratorDetails =
            res.data.profileConfiguratorDetails;
          setTheme(profileConfiguratorDetails.theme);
          setAppTheme(profileConfiguratorDetails.theme);
          checkSiteIcon(profileConfiguratorDetails.siteIcon);
          setScreenOption(profileConfiguratorDetails.screenOption);
          if (profileConfiguratorDetails.siteTimeZone) {
            // setSiteTimezone(profileConfiguratorDetails.siteTimeZone);
          }
        } else {
          setIsTemaplateAvailable(false);
          const errorMsg = getErrorMsg(res.code);
          setErrorMessage(errorMsg);
        }
      } else {
        const data = await response.json();
        if (data.code === "TokenExpired") {
          getNewToken();
        } else {
          setErrorScreen(true);
          afterFetchResponse();
        }
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setErrorScreen(true);
      afterFetchResponse();
    }
  };
  const getNewToken = async () => {
    await getTokenTemplateData(deviceId, false);
  };
  // To Get the Graph Data
  const getDigitalSignageGraphData = async (
    configuredTemplateId: string,
    loaderValue: boolean
  ) => {
    setGraphLoading(loaderValue);
    const url =
      `${window.ENV.BASE_URL_API_GATEWAY}` +
      "/api/v2/CompositeService/GetTemplateDataConsumption/" +
      configuredTemplateId;
    try {
      const responseGraphData = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          // 'X-Token': `Bearer ${token}`,
          "X-NodeId": siteId,
          "X-DeviceId": deviceId,
        },
      });
      if (responseGraphData.ok) {
        
        const res = await responseGraphData.json();
        const { electricity, gas, water, carbon, savings } = res;
      
        // Create an array of enabled components
        const enabledComponents = [
          electricity?.isEnabled && 0, // Electricity corresponds to index 0
          gas?.isEnabled && 1,         // Gas corresponds to index 1
          water?.isEnabled && 2,       // Water corresponds to index 2
          carbon?.isEnabled && 3,      // Carbon corresponds to index 3
          savings?.isEnabled && 4,     // Savings corresponds to index 4
        ].filter((index) => index !== false); // Filter out disabled components
    
        const enabledComponentsRemoveUndefined = enabledComponents.filter(
          (val) => val !== undefined
        );
        setCurrentComponentIndex(enabledComponentsRemoveUndefined[0]);
        setGraphLoading(false);
        setDIgitalSignageData(res);
        setGraphDataErrorScreen(false);
        navigate("/");
      } else {
        const data = await responseGraphData.json();
        if (data.code === "TokenExpired") {
          getNewToken();
        } else {
          const retyresponse = await retryFunction(url);
          if (retyresponse.isSuccess) {
            setDIgitalSignageData(retyresponse);
            setGraphDataErrorScreen(false);
          } else {
            setGraphDataErrorScreen(true);
          }
        }
        setGraphLoading(false);
      }
    } catch (err) {
      setGraphLoading(false);
      setGraphDataErrorScreen(true);
      const retyresponse = await retryFunction(url);
      if (retyresponse.isSuccess) {
        setDIgitalSignageData(retyresponse);
        setGraphDataErrorScreen(false);
      } else {
        setGraphDataErrorScreen(true);
      }
    }
  };
  const retryFunction = async (url: string, fetchMethod: string = "GET") => {
    let headers = {
      Authorization: `Bearer ${token}`,
      "X-NodeId": siteId,
      "X-DeviceId": deviceId,
    };
    return fetchURL(url, {
      method: fetchMethod,
      retries: 3,
      retryOn: [
        number400,
        number404,
        number408,
        number500,
        number502,
        number503,
        number504,
        number522,
        number524,
      ],
      // retryDelay: 10000,
      retryDelay: function (
        attempt: number,
        error: Error | null,
        response: Response | null
      ) {
        return Math.pow(number2, attempt) * number10000; // 1000, 2000, 4000
      },
      headers: headers,
    })
      .then(function (response: Response) {
        return response.json();
      })
      .catch(function (err: Error | null) {
        return err;
      });
  };
useEffect(() => {
   if (!errorScreen && !activationCodeErrorScreen && !graphDataErrorScreen) {
    setDisplayErrorScreen(false);
  } else {
    setDisplayErrorScreen(true);
  }
}, [errorScreen, activationCodeErrorScreen, graphDataErrorScreen]);
  // const conditionsToCheckLoader = [
  //   !errorScreen,
  //   !activationCodeErrorScreen,
  //   !graphDataErrorScreen
  // ];
  
  // console.log("conditionsToCheckLoader",  !errorScreen,!activationCodeErrorScreen,!graphDataErrorScreen);
  // if (conditionsToCheckLoader.every((condition) => condition)) {
  //   displayErrorScreen = false;
  // } else {
  //   displayErrorScreen = true;
  // }
  const conditionsToCheckSlider = [
    !loading,
    !graphLoading,
    !activationCodeLoading
  ];
  if (conditionsToCheckSlider.every((condition) => condition)) {
    dsLoading = false;
  } else {
    dsLoading = true;
  }
  const getSiteImage = () => {
    if (siteImage) {
      return siteImage;
    } else {
      return "";
    }
  };
const formatValue = (value: number): string => {
    if (value >= 1_000_000_000_000_000) {
    // 1,000,000,000,000,000 and above: T (trillion) with comma
    return `${Math.round(value / 1_000_000_000_000).toLocaleString()}T`;
  }else if(value >= 100_000_000_000_000){
  return `${Math.round(value / 1_000_000_000_000).toLocaleString()}T`; // Trillions as T with comma
  } else if (value >= 1_000_000_000_000) {
    return `${Math.round(value / 1_000_000_000).toLocaleString()}B`; // Trillions as B with comma
  } else if (value >= 100_000_000_000) {
    return `${Math.round(value / 1_000_000_000)}B`; // Hundreds of billions as B
  } else if (value >= 1_000_000_000) {
    return `${Math.round(value / 1_000_000).toLocaleString()}M`; // Billions as M with comma
  } else if (value >= 100_000_000) {
    return `${Math.round(value / 1_000_000)}M`; // Show in M for 100,000,000 and above
  } else if (value >= 1_000_000) {
    return `${Math.round(value / 1_000).toLocaleString()}K`; // Show in K with comma for millions
  } else if (value >= 100_000) {
    return `${Math.round(value / 1_000)}K`; // Show in K for 100,000 to 999,999
  } else if (value >= 1_000) {
    return value.toLocaleString(); // Show with comma for thousands
  } else {
    return value.toLocaleString(); // Format numbers below 1,000 with commas
  }
}
  const renderTemplate = () => {
    if (isTemaplateAvailable) {
      if (getDIgitalSignageData && !loading) {
        const { electricity, gas, water, carbon, savings } =
          getDIgitalSignageData;
        const actualPerformance =
          electricity && electricity.actualPerformance
            ? electricity.actualPerformance
            : [];
        const baselinePerformance =
          electricity && electricity.baselinePerformance
            ? electricity.baselinePerformance
            : [];
        const actualCost =
          savings && savings.actualCost ? savings.actualCost : [];
        const baselineCost =
          savings && savings.baselineCost ? savings.baselineCost : [];
        const actualPerformanceGas =
          gas && gas.actualPerformance ? gas.actualPerformance : [];
        const baselinePerformanceGas =
          gas && gas.baselinePerformance ? gas.baselinePerformance : [];
        const actualPerformanceWater =
          water && water.actualPerformance ? water.actualPerformance : [];
        const baselinePerformanceWater =
          water && water.baselinePerformance ? water.baselinePerformance : [];
        const actualPerformanceCarbon =
          carbon && carbon.actualPerformance ? carbon.actualPerformance : [];
        const baselinePerformanceCarbon =
          carbon && carbon.baselinePerformance
            ? carbon.baselinePerformance
            : [];
        const unitElectricity =
          electricity && electricity.unit ? electricity.unit : "";
        const unitGas = gas && gas.unit ? gas.unit : "";
        const unitWater = water && water.unit ? water.unit : "";
        const unitCarbon = carbon && carbon.unit ? carbon.unit : "";
        const unitSavings = savings && savings.unit ? savings.unit : "USD";

        const gallonsOfGas = getDIgitalSignageData.gallonsOfGas
          ? isNotNegative(Number(getDIgitalSignageData.gallonsOfGas))
            ? formatValue(Number(getDIgitalSignageData.gallonsOfGas))
            : "--"
          : "--";
        const carsRemoved = getDIgitalSignageData.carsRemoved
          ? isNotNegative(Number(getDIgitalSignageData.carsRemoved))
            ? formatValue(Number(getDIgitalSignageData.carsRemoved))
            : "--"
          : "--";
        const absorptionCount = getDIgitalSignageData.absorptionCount
          ? isNotNegative(Number(getDIgitalSignageData.absorptionCount))
            ? formatValue(Number(getDIgitalSignageData.absorptionCount))
            : "--"
          : "--";
        const siteName =
          getDIgitalSignageData.siteInformation &&
          getDIgitalSignageData.siteInformation.siteName
            ? getDIgitalSignageData.siteInformation.siteName
            : "--";

        switch (currentComponentIndex) {
          case 0:
            return (
              <div
                className={`${
                  theme === "Dark"
                    ? "digital-dark-signage-card"
                    : "digital-light-signage-card"
                }`}
              >
                <DigitalSignageAreaHeader
                  theme={theme === "Dark" ? true : false}
                  siteName={siteName}
                  gallonsOfGas={gallonsOfGas}
                  carsRemoved={carsRemoved}
                  absorptionCount={absorptionCount}
                  siteImage={getSiteImage()}
                />
                <DigitalSignageAreaGraph
                  theme={theme === "Dark" ? true : false}
                  tab={currentComponentIndex}
                  type={ConversionType.ELECTRICITY}
                  unit={unitElectricity}
                  data={{
                    actualPerformance: actualPerformance,
                    baselinePerformance: baselinePerformance,
                  }}
                />
                <DigitalSignageAreaFooter
                  theme={theme === "Dark" ? true : false}
                  highlightedIndex={currentComponentIndex}
                  getDIgitalSignageData={getDIgitalSignageData}
                />
              </div>
            );
          case 1:
            return (
              <div
                className={`${
                  theme === "Dark"
                    ? "digital-dark-signage-card"
                    : "digital-light-signage-card"
                }`}
              >
                <DigitalSignageAreaHeader
                  theme={theme === "Dark" ? true : false}
                  siteName={siteName}
                  gallonsOfGas={gallonsOfGas}
                  carsRemoved={carsRemoved}
                  absorptionCount={absorptionCount}
                  siteImage={getSiteImage()}
                />
                <DigitalSignageAreaGraph
                  theme={theme === "Dark" ? true : false}
                  tab={currentComponentIndex}
                  type={ConversionType.GAS}
                  unit={unitGas}
                  data={{
                    actualPerformance: actualPerformanceGas,
                    baselinePerformance: baselinePerformanceGas,
                  }}
                />
                <DigitalSignageAreaFooter
                  theme={theme === "Dark" ? true : false}
                  highlightedIndex={currentComponentIndex}
                  getDIgitalSignageData={getDIgitalSignageData}
                />
              </div>
            );
          case 2:
            return (
              <div
                className={`${
                  theme === "Dark"
                    ? "digital-dark-signage-card"
                    : "digital-light-signage-card"
                }`}
              >
                <DigitalSignageAreaHeader
                  theme={theme === "Dark" ? true : false}
                  siteName={siteName}
                  gallonsOfGas={gallonsOfGas}
                  carsRemoved={carsRemoved}
                  absorptionCount={absorptionCount}
                  siteImage={getSiteImage()}
                />
                <DigitalSignageAreaGraph
                  theme={theme === "Dark" ? true : false}
                  tab={currentComponentIndex}
                  type={ConversionType.WATER}
                  unit={unitWater}
                  data={{
                    actualPerformance: actualPerformanceWater,
                    baselinePerformance: baselinePerformanceWater,
                  }}
                />
                <DigitalSignageAreaFooter
                  theme={theme === "Dark" ? true : false}
                  highlightedIndex={currentComponentIndex}
                  getDIgitalSignageData={getDIgitalSignageData}
                />
              </div>
            );
          case 3:
            return (
              <div
                className={`${
                  theme === "Dark"
                    ? "digital-dark-signage-card"
                    : "digital-light-signage-card"
                }`}
              >
                <DigitalSignageAreaHeader
                  theme={theme === "Dark" ? true : false}
                  siteName={siteName}
                  gallonsOfGas={gallonsOfGas}
                  carsRemoved={carsRemoved}
                  absorptionCount={absorptionCount}
                  siteImage={getSiteImage()}
                />
                <DigitalSignageAreaGraph
                  theme={theme === "Dark" ? true : false}
                  tab={currentComponentIndex}
                  type={ConversionType.CARBON}
                  unit={unitCarbon}
                  data={{
                    actualPerformance: actualPerformanceCarbon,
                    baselinePerformance: baselinePerformanceCarbon,
                  }}
                />
                <DigitalSignageAreaFooter
                  theme={theme === "Dark" ? true : false}
                  highlightedIndex={currentComponentIndex}
                  getDIgitalSignageData={getDIgitalSignageData}
                />
              </div>
            );
          case 4:
            return (
              <div
                className={`${
                  theme === "Dark"
                    ? "digital-dark-signage-card"
                    : "digital-light-signage-card"
                }`}
              >
                <DigitalSignageAreaHeader
                  theme={theme === "Dark" ? true : false}
                  siteName={siteName}
                  gallonsOfGas={gallonsOfGas}
                  carsRemoved={carsRemoved}
                  absorptionCount={absorptionCount}
                  siteImage={getSiteImage()}
                />
                <DigitalSignageAreaGraph
                  theme={theme === "Dark" ? true : false}
                  tab={currentComponentIndex}
                  type="cost"
                  unit={unitSavings}
                  data={{
                    actualPerformance: actualCost,
                    baselinePerformance: baselineCost,
                  }}
                />
                <DigitalSignageAreaFooter
                  theme={theme === "Dark" ? true : false}
                  highlightedIndex={currentComponentIndex}
                  getDIgitalSignageData={getDIgitalSignageData}
                />
              </div>
            );
          default:
            return  <DSNotConfiguredLandscape
          title={'Oops! No Data Found'}
          text="Please contact your facility manager for more details."
        />;
        }
      }
    } else {
      return (
        <DSNotConfiguredLandscape
          title={errorMessage}
          text="Please contact your facility manager for more details."
        />
      );
    }
  };
  return (
    <div
      className={`${
        theme === "Dark"
          ? "digital-signage-view-dark-container"
          : "digital-signage-view-light-container"
      }`}
    >
      {displayErrorScreen && (
        <DSNotConfiguredLandscape
          title={titleErrMsg}
          text={
            `${textErrMsg}` +
            `\n` +
            `Please contact your facility manager for more details.`
          }
        />
      )}
      <Loader loading={dsLoading} text="Loading Digital Signage">
        {!displayErrorScreen && !dsLoading && <>{renderTemplate()}</>}
      </Loader>
    </div>
  );
}
export default DigitalSignageViewContainer;
